<template>
  <div class="app-layout">
    <LayoutHeader />
    <slot />
    <LayoutFooter />
  </div>
</template>

<script setup>
import useLanguageStore from '~/store/language';
import useFeaturesStore from '~/store/features';
import useProfileStore from '~/store/profile';
import useAnalyticsStore from '~/store/analytics';

import {
  STORAGE_LANGUAGE_KEY,
  STORAGE_AUTH_TOKEN,
  STORAGE_DISTINCT_ID,
  MAX_AGE,
} from '~/constants/cookieStorage';

import { v4 as uuidv4 } from 'uuid';
import { useMediaQuery } from '@vueuse/core';
import { provide } from 'vue';

import camelcaseKeys from 'camelcase-keys';

const { $useApi } = useNuxtApp();

const { $switchLocale } = useI18n();

const featuresStore = useFeaturesStore();
const languageStore = useLanguageStore();
const profileStore = useProfileStore();

await useAsyncData('features', async () => {
  const headers = useCustomRequestHeaders();
  const features = await $fetch($useApi.features.getFeatures, {
    headers,
  });
  featuresStore.setFeatures(camelcaseKeys(features, { deep: true }));
  return features;
});
await useAsyncData('language', () => setLanguage());
await useAsyncData('profile', () => setUserProfile());

onMounted(() => {
  if (profileStore.isAuth) {
    useAnalyticsStore().setUserId(profileStore.profile.id);
  }

  const language = getLanguageFromCookie() || getLanguageFromFeatures();
  applyBodyArmClass(language);
});

function setLanguage() {
  const language = getLanguageFromCookie() || getLanguageFromFeatures();
  $switchLocale(language);
  languageStore.setLanguage(language);

  applyBodyArmClass(language);
  return language;
}

function applyBodyArmClass(language) {
  if (language === 'hy') {
    document.body.classList.add('arm');
  } else {
    document.body.classList.remove('arm');
  }
}

async function setUserProfile() {
  const token = useCookie(STORAGE_AUTH_TOKEN).value;
  const distinctId = useCookie(STORAGE_DISTINCT_ID).value || uuidv4();

  useCookie(STORAGE_DISTINCT_ID, {
    maxAge: MAX_AGE,
  }).value = distinctId;
  profileStore.setDistinctId(distinctId);

  if (token) {
    profileStore.setToken(token);
    const headers = useCustomRequestHeaders();
    const profile = await $fetch($useApi.profile.getProfile, {
      headers,
    });

    const formatedProfile = camelcaseKeys(profile, { deep: true });

    profileStore.setProfile({
      profile: formatedProfile,
    });

    return formatedProfile;
  }

  return {};
}

function getLanguageFromCookie() {
  const languageFromCookie = useCookie(STORAGE_LANGUAGE_KEY);
  return languageFromCookie.value;
}

function getLanguageFromFeatures() {
  return featuresStore.defaultLanguage;
}

const isMobile = useMediaQuery('(max-width: 767px)', {
  ssrWidth: 375,
});
provide('isMobile', isMobile);
</script>
